import { roseStorage } from '@inboundx/shared/platform';
import * as Sentry from '@sentry/react';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';
import {
    fetchWorkspaces,
    isPathWorkspace,
    WORKSPACE_DOMAIN_URL_PARAM as URL_PARAM,
    WORKSPACE_ID_URL_PARAM as WORKSPACE_URL_PARAM,
    type Workspace,
} from '@/services/workspaces';

/**
 * Resolve a URL or stored selection. It may be a workspace id, or a hostname from before
 * workspaces were selectable; a hostname means the workspace that owns the whole host.
 */
export function resolveWorkspaceSelection(
    workspaces: Workspace[],
    ...candidates: (string | null)[]
): Workspace | undefined {
    for (const candidate of candidates) {
        if (!candidate) continue;
        const match =
            workspaces.find((w) => w.id === candidate) ??
            workspaces.find((w) => w.domain === candidate && !isPathWorkspace(w));
        if (match) return match;
    }
    return undefined;
}

interface WorkspaceContextValue {
    /** Key of workspace-owned data (knowledge, FAQs, datasets…). Empty until one is selected. */
    selectedWorkspaceId: string;
    /**
     * Host of the selected workspace's canonical root, for hostname-keyed readers
     * (analytics, tracking). Two workspaces on one host share it by design.
     */
    selectedDomain: string;
    /**
     * Key of the selected workspace's rows in `config.client_configs`. Equals
     * `selectedDomain` for a workspace that owns its whole host; an opaque slug for a
     * workspace placed under a path, which inherits its host's configuration.
     */
    selectedConfigKey: string;
    /**
     * Accepts a workspace id, or a hostname for the workspace that owns that whole host.
     * Returns false when nothing selectable matches (a video client, a workspace without
     * a canonical root, a host that only has workspaces under a path). The previous
     * selection then stays, so a caller that navigates next must check the result or it
     * opens another client's screen.
     */
    selectWorkspace: (workspaceIdOrHost: string) => boolean;
    workspaces: Workspace[];
    loading: boolean;
    /** True while queries are refetching after a workspace switch. */
    switching: boolean;
    error: string | null;
    selectedWorkspace: Workspace | null;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const { accessibleDomains, loading: authLoading } = useBackofficeAuth();
    const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
    const [selectedWorkspaceId, setSelectedWorkspaceIdState] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [switching, setSwitching] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fetchingCount = useIsFetching();
    const switchingRef = useRef(false);

    /** Select a workspace and mirror it to telemetry, the URL and storage. */
    const applySelection = useCallback(
        (workspace: Workspace) => {
            setSelectedWorkspaceIdState(workspace.id);
            // Telemetry stays keyed by host so existing dashboards keep their series.
            if (posthog.__loaded) {
                posthog.group('domain', workspace.domain);
            }
            Sentry.setTag('domain', workspace.domain);
            Sentry.setTag('workspace_id', workspace.id);

            // Functional updater avoids a stale searchParams.
            setSearchParams(
                (prev) => {
                    const pathRooted = isPathWorkspace(workspace);
                    if (
                        prev.get(URL_PARAM) === workspace.domain &&
                        prev.get(WORKSPACE_URL_PARAM) === (pathRooted ? workspace.id : null)
                    ) {
                        return prev;
                    }
                    const next = new URLSearchParams(prev);
                    next.set(URL_PARAM, workspace.domain);
                    if (pathRooted) {
                        next.set(WORKSPACE_URL_PARAM, workspace.id);
                    } else {
                        next.delete(WORKSPACE_URL_PARAM);
                    }
                    return next;
                },
                { replace: true }
            );

            // The stored value is a workspace id; an older stored hostname still resolves.
            roseStorage.setBackofficeSelectedDomain(workspace.id);
        },
        [setSearchParams]
    );

    // Load workspaces when their legacy domain access grants are available.
    // biome-ignore lint/correctness/useExhaustiveDependencies: searchParams/setSearchParams are stable React Router refs — adding them would cause unnecessary re-runs
    useEffect(() => {
        async function loadWorkspaces() {
            // Wait for auth to complete
            if (authLoading) {
                return;
            }

            // No accessible domains means there are no selectable workspaces.
            if (accessibleDomains.length === 0) {
                setWorkspaces([]);
                setSelectedWorkspaceIdState('');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // RLS (has_site_access) limits the rows to accessible workspaces.
                const workspaceRecords = await fetchWorkspaces();

                // Belt and suspenders: workspace access derives from the grant on its host.
                const accessibleWorkspaces = workspaceRecords.filter((workspace) =>
                    accessibleDomains.includes(workspace.domain)
                );

                setWorkspaces(accessibleWorkspaces);

                if (accessibleWorkspaces.length > 0) {
                    // Priority: URL params > roseStorage > first workspace
                    const initial =
                        resolveWorkspaceSelection(
                            accessibleWorkspaces,
                            searchParams.get(WORKSPACE_URL_PARAM),
                            searchParams.get(URL_PARAM),
                            roseStorage.getBackofficeSelectedDomain()
                        ) ?? accessibleWorkspaces[0];

                    applySelection(initial);
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load workspaces');
            } finally {
                setLoading(false);
            }
        }

        loadWorkspaces();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessibleDomains, authLoading]);

    // Clear switching state once all queries have settled
    useEffect(() => {
        if (switchingRef.current && fetchingCount === 0) {
            switchingRef.current = false;
            setSwitching(false);
        }
    }, [fetchingCount]);

    const selectWorkspace = useCallback(
        (workspaceIdOrHost: string) => {
            const workspace = resolveWorkspaceSelection(workspaces, workspaceIdOrHost);
            if (!workspace) return false;
            if (workspace.id === selectedWorkspaceId) return true;

            switchingRef.current = true;
            setSwitching(true);
            applySelection(workspace);

            // Invalidate all queries so the newly selected workspace reloads its data.
            queryClient.invalidateQueries();
            return true;
        },
        [selectedWorkspaceId, workspaces, applySelection, queryClient]
    );

    const selectedWorkspace = useMemo(() => {
        return workspaces.find((workspace) => workspace.id === selectedWorkspaceId) || null;
    }, [workspaces, selectedWorkspaceId]);
    const selectedDomain = selectedWorkspace?.domain ?? '';
    const selectedConfigKey = selectedWorkspace?.configKey ?? '';

    const value = useMemo(
        () => ({
            selectedWorkspaceId,
            selectedDomain,
            selectedConfigKey,
            selectWorkspace,
            workspaces,
            loading: authLoading || loading,
            switching,
            error,
            selectedWorkspace,
        }),
        [
            selectedWorkspaceId,
            selectedDomain,
            selectedConfigKey,
            selectWorkspace,
            workspaces,
            authLoading,
            loading,
            switching,
            error,
            selectedWorkspace,
        ]
    );

    return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
    const context = useContext(WorkspaceContext);
    if (!context) {
        throw new Error('useWorkspace must be used within a WorkspaceProvider');
    }
    return context;
}

/** Like `useWorkspace`, but returns null instead of throwing when there is no
 *  provider — for chrome that also renders outside the app shell (onboarding,
 *  the welcome flow), where no workspace is selected yet. */
export function useOptionalWorkspace(): WorkspaceContextValue | null {
    return useContext(WorkspaceContext);
}
